import { WIDGET_METRIC_GROUPS } from "@services/math/math.const";
import { addDays, format } from "date-fns";
interface ChartDataPoint {
  date: string;
  [key: string]: number | string;
}

export const data = [
  { date: "1 Jan", AdsSales: 200, OrganicSales: 120 },
  { date: "2 Jan", AdsSales: 180, OrganicSales: 140 },
  { date: "3 Jan", AdsSales: 160, OrganicSales: 150 },
  { date: "4 Jan", AdsSales: 230, OrganicSales: 190 },
  { date: "5 Jan", AdsSales: 190, OrganicSales: 110 },
  { date: "6 Jan", AdsSales: 220, OrganicSales: 130 },
  { date: "7 Jan", AdsSales: 210, OrganicSales: 170 },
];

export const COLORS = ["#5295E0", "#80C67A"];
export const legendColorMapping: Record<string, string> = {
  AdsSales: "#5295E0",
  OrganicSales: "#80C67A",
};
export const nameMapping: Record<string, string> = {
  AdsSales: "AdsSales",
  OrganicSales: "OrganicSales",
};

export function generateDateRangeArray(
  startDate: string | Date,
  endDate: string | Date,
) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const dates = [];

  const current = new Date(start);

  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

export const renderCustomizedBar = (
  props: any,
  activeLabel: string | null,
  color: string,
  isForecasted: boolean,
) => {
  const { x, y, width, height, payload } = props;
  const isActive = payload.date === activeLabel;
  const fillOpacity = isForecasted
    ? 0.5
    : activeLabel === null
      ? 1
      : isActive
        ? 1
        : 0.3;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={color}
        fillOpacity={fillOpacity}
        strokeDasharray={isForecasted ? "5 5" : "0"}
        stroke={isForecasted ? color : "none"}
      />
    </g>
  );
};

export type SalesChecks = {
  [key: string]: boolean;
};

export const initialChecks: SalesChecks = {
  totalSales: true,
  adSales: true,
  adSpend: true,
  adImpressions: true,
  adCtr: true,
  adCvr: true,
};

export const getInitialChecks = (groups: string[] | "all"): SalesChecks => {
  if (Array.isArray(groups) && groups.length === 1) {
    const keys = WIDGET_METRIC_GROUPS.filter(
      (group) => group.title === groups[0],
    )
      .map((metricGroup) => metricGroup.keys)
      .flat()
      .slice(0, 6);

    const initialChecks: SalesChecks = {};

    keys.forEach((key) => {
      initialChecks[key] = true;
    });
    return initialChecks;
  }
  return initialChecks;
};

export const initialChecksBrand: SalesChecks = {
  totalSales: true,
  adSales: false,
  adSpend: false,
  adImpressions: false,
  adCtr: false,
  adCvr: false,
};

export const initialChecksMarket: SalesChecks = {
  totalSales: true,
  adSales: false,
  adSpend: false,
  adImpressions: false,
  adCtr: false,
  adCvr: false,
};

export const generateData = (
  startDate: Date,
  endDate: Date,
  activeChecks: string[],
) => {
  const dates = generateDateRangeArray(startDate, endDate);

  return dates.map((date) => {
    const obj: any = {
      date: format(date, "d MMM"),
    };

    activeChecks.forEach((check) => {
      obj[check] = Math.floor(Math.random() * 1000);
    });

    return obj;
  });
};

export function modifyTooltipPayload(payload) {
  if (!payload) return payload;

  const filteredPayload = payload.filter(
    (item) => !String(item?.dataKey ?? "").endsWith("_forecast"),
  );

  return filteredPayload.map((item) => {
    if (!item) return item;
    const newDataKey =
      typeof item.dataKey === "string"
        ? item.dataKey.replace(/_(main|forecast)$/, "")
        : item.dataKey;
    const newName =
      typeof item.name === "string"
        ? item.name.replace(/_(main|forecast)$/, "")
        : item.name;
    return {
      ...item,
      dataKey: newDataKey,
      name: newName,
    };
  });
}

export function mockDataCompareWithOffset(
  mainData: ChartDataPoint[],
  compareStart: Date,
): ChartDataPoint[] {
  return mainData.map((point, i) => {
    const currentDay = addDays(compareStart, i);
    const newDateString = format(currentDay, "MMM dd");
    const newPoint: ChartDataPoint = {};

    Object.keys(point).forEach((key) => {
      if (key === "date") {
        newPoint.date = newDateString;
      } else {
        const originalVal = Number(point[key] ?? 0);
        const randomCoef = 0.6 + Math.random() * 0.8;
        newPoint[key] = +(originalVal * randomCoef).toFixed(2);
      }
    });

    return newPoint;
  });
}

export function mergeMainWithCompare(
  main: ChartDataPoint[],
  compare: ChartDataPoint[],
): ChartDataPoint[] {
  return [...main, ...compare];
}
